import axios from "axios";
import { API_URL, getAuthHeader, getPDFAuthHeader } from ".";
import { ResponseModel } from "../Models/ResponseModel";
import { Event } from "../Models/Event";
import { User } from "../Models/User";
import { EventParticipant } from "../Models/EventParticipant";
import { ConferenceMeeting } from "../Models/ConferenceMeeting";

export type RegisteredCompany = {
  memberId?: number | null;
  name: string;
  logoUrl?: string | null;
  website?: string | null;
  city?: string | null;
  state?: string | null;
  description?: string | null;
  phone?: string | null;
  industry?: string | null;
};

export type RegisteredCompaniesResponse = {
  eventId: number;
  eventName: string;
  companies: RegisteredCompany[];
};

class EventApi {
  static getEvent(id: string) {
    return axios.get<Event>(`${API_URL}/api/event/${id}`, getAuthHeader());
  }
  static getEventParticipant(id: number, userId: number) {
    return axios.get<EventParticipant>(
      `${API_URL}/api/event/${id}/participant/${userId}`,
      getAuthHeader()
    );
  }
  static getEventList() {
    return axios.get<Event[]>(`${API_URL}/api/event/list`, getAuthHeader());
  }
  static getUserEventList(userId: string) {
    return axios.get<Event[]>(
      `${API_URL}/api/event/list/user/${userId}`,
      getAuthHeader()
    );
  }
  static addUpdateEvent(request: Event) {
    return axios.post<ResponseModel>(
      `${API_URL}/api/event`,
      request,
      getAuthHeader()
    );
  }
  static deleteEvent(id: number) {
    return axios.delete<ResponseModel>(
      `${API_URL}/api/event/${id}`,
      getAuthHeader()
    );
  }
  static addUserToEvent(eventId: number, request: User) {
    return axios.post<ResponseModel>(
      `${API_URL}/api/event/${eventId}`,
      request,
      getAuthHeader()
    );
  }
  static sendReminderNow(eventId: number) {
    return axios.post<ResponseModel>(
      `${API_URL}/api/event/${eventId}/send-reminder`,
      {},
      getAuthHeader()
    );
  }
  static getEventReport(eventId: number) {
    return axios.get(
      `${API_URL}/api/Reports/event/${eventId}`,
      getPDFAuthHeader()
    );
  }
  static getEventParticipants(eventId: number) {
    return axios.get<EventParticipant[]>(
      `${API_URL}/api/event/participants/${eventId}`,
      getAuthHeader()
    );
  }
  /** Public: distinct registered companies (no individual attendees). */
  static getRegisteredCompanies(eventId: number) {
    return axios.get<RegisteredCompaniesResponse>(
      `${API_URL}/api/event/${eventId}/registered-companies`
    );
  }
  static deleteEventParticipant(eventId: number, participantId: number) {
    return axios.delete<ResponseModel>(
      `${API_URL}/api/event/${eventId}/participant/${participantId}`,
      getAuthHeader()
    );
  }
  static getEventParticipantBadges(eventId: number, participantIds: number[]) {
    return axios.post(
      `${API_URL}/api/event/${eventId}/participant-badges`,
      participantIds,
      getPDFAuthHeader()
    );
  }
  /** Printable conference agenda PDF (sessions + meetings, Eastern Time). */
  static getConferenceAgendaPdf(conferenceId: number) {
    return axios.get(
      `${API_URL}/api/Reports/conference/${conferenceId}/agenda`,
      getPDFAuthHeader()
    );
  }
  // Get current/active conference for a season and year
  static getCurrentConference(season: string, year: number) {
    return axios.get<Event>(
      `${API_URL}/api/event/conference/current?season=${season}&year=${year}`,
      getAuthHeader()
    );
  }
  // Get all sub-events for a conference
  static getConferenceSubEvents(conferenceId: number) {
    return axios.get<Event[]>(
      `${API_URL}/api/event/${conferenceId}/subevents`,
      getAuthHeader()
    );
  }

  /** All scheduled meetings tied to this conference (any member). */
  static getConferenceMeetings(conferenceId: number) {
    return axios.get<ConferenceMeeting[]>(
      `${API_URL}/api/conference/${conferenceId}/meetings`,
      getAuthHeader()
    );
  }
  // Get conference by year and season
  static getConferenceByYearAndSeason(year: number, season: string) {
    return axios.get<Event>(
      `${API_URL}/api/event/conference/current?season=${season}&year=${year}`,
      getAuthHeader()
    );
  }
  // Get all conferences
  static getAllConferences() {
    return axios.get<Event[]>(
      `${API_URL}/api/event/conferences/all`,
      getAuthHeader()
    );
  }
  // Get most recent Spring conference
  static getMostRecentSpringConference() {
    return axios.get<Event>(
      `${API_URL}/api/event/conference/spring/most-recent`,
      getAuthHeader()
    );
  }
  // Get most recent Fall conference
  static getMostRecentFallConference() {
    return axios.get<Event>(
      `${API_URL}/api/event/conference/fall/most-recent`,
      getAuthHeader()
    );
  }
  // Copy conference with all events and pages (super users only)
  static copyConference(sourceConferenceId: number) {
    return axios.post<ResponseModel>(
      `${API_URL}/api/event/conference/copy/${sourceConferenceId}`,
      {},
      getAuthHeader()
    );
  }

  static getContactSessionMap(eventId: number) {
    return axios.get<ContactSessionMap>(
      `${API_URL}/api/event/${eventId}/contact-session-map`
    );
  }

  static getContactSessionBooth(eventId: number, boothId: number) {
    return axios.get<ContactSessionBoothDetail>(
      `${API_URL}/api/event/${eventId}/contact-session-map/booths/${boothId}`
    );
  }

  static parseContactSession(eventId: number, imageUrl: string) {
    return axios.post<ContactSessionDraft>(
      `${API_URL}/api/event/${eventId}/contact-session/parse`,
      { imageUrl },
      getAuthHeader()
    );
  }

  static parseContactSessionFile(eventId: number, file: FormData) {
    return axios.post<ContactSessionDraft>(
      `${API_URL}/api/event/${eventId}/contact-session/parse-file`,
      file,
      getAuthHeader()
    );
  }

  static saveContactSession(eventId: number, draft: ContactSessionDraft) {
    return axios.put<ResponseModel>(
      `${API_URL}/api/event/${eventId}/contact-session`,
      draft,
      getAuthHeader()
    );
  }
}

export type ContactSessionBooth = {
  id?: number;
  label: string;
  displayName: string;
  memberId?: number | null;
  regionJson?: string | null;
};

export type ContactSessionMap = {
  eventId: number;
  eventName?: string | null;
  floorplanUrl?: string | null;
  booths: ContactSessionBooth[];
};

export type ContactSessionAttendee = {
  firstName?: string | null;
  lastName?: string | null;
  role: string;
};

export type ContactSessionBoothDetail = {
  displayName: string;
  memberId?: number | null;
  memberActive?: boolean;
  city?: string | null;
  state?: string | null;
  phone?: string | null;
  website?: string | null;
  logoUrl?: string | null;
  headline?: string | null;
  conferenceName?: string | null;
  participants: ContactSessionAttendee[];
};

export type ContactSessionDraft = {
  floorplanUrl?: string | null;
  booths: ContactSessionBooth[];
};

export default EventApi;
